import i18next from "i18next";
import type React from "react";
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { type LanguageKey, languages } from "../i18n/index.ts";

interface LanguageContextType {
	currentLanguage: LanguageKey;
	changeLanguage: (lang: LanguageKey) => void;
	languages: typeof languages;
}

const LanguageContext = createContext<LanguageContextType>({
	currentLanguage: "en_US",
	changeLanguage: () => {},
	languages,
});

const LANGUAGE_STORAGE_KEY = "comms-language";

export function LanguageProvider({ children }: { children: React.ReactNode }) {
	const [currentLanguage, setCurrentLanguage] = useState<LanguageKey>(() => {
		const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY) as LanguageKey | null;
		return stored && languages[stored] ? stored : "en_US";
	});

	const changeLanguage = useCallback((lang: LanguageKey) => {
		if (languages[lang]) {
			setCurrentLanguage(lang);
			i18next.changeLanguage(lang);
			localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
			document.documentElement.lang = lang.split("_")[0] ?? "en";
		}
	}, []);

	useEffect(() => {
		changeLanguage(currentLanguage);
	}, [currentLanguage, changeLanguage]);

	return (
		<LanguageContext.Provider value={{ currentLanguage, changeLanguage, languages }}>
			{children}
		</LanguageContext.Provider>
	);
}

export function useLanguage() {
	return useContext(LanguageContext);
}
